import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { fetchApi } from '../config/api';
import { SUPPORTED_LANGUAGES, SupportedLanguage } from '../i18n';
import { LANGUAGES } from '../utils/language';
import { AdminPregnancyDay, PregnancyDayTranslation } from '../utils/pregnancy';
import { Alert, Input, Modal, SegmentedTabs, Textarea } from './UI';

interface Props {
  /** null = geschlossen. */
  day: number | null;
  /** Bereits geladene Texte (z.B. aus der Admin-Liste) - ohne sie werden sie für den Tag nachgeladen. */
  initial?: AdminPregnancyDay | null;
  onClose: () => void;
  onSaved: (updated: AdminPregnancyDay) => void;
}

const TOPIC_MAX_LENGTH = 200;

const withAllLanguages = (translations: Record<string, PregnancyDayTranslation> | undefined) =>
  Object.fromEntries(
    SUPPORTED_LANGUAGES.map((lang) => [lang, { topic: translations?.[lang]?.topic ?? '', description: translations?.[lang]?.description ?? '' }])
  ) as Record<SupportedLanguage, PregnancyDayTranslation>;

const isComplete = (translation: PregnancyDayTranslation | undefined) =>
  !!translation?.topic.trim() && !!translation?.description.trim();

/** Bearbeitet "Thema des Tages" und Beschreibung eines Trächtigkeitstags in allen vier Sprachen auf einmal -
 * genutzt im Adminbereich und direkt aus dem Trächtigkeitskalender heraus (nur für Administratoren). */
export function PregnancyDayEditor({ day, initial, onClose, onSaved }: Props) {
  const { t, i18n } = useTranslation();
  const [language, setLanguage] = useState<SupportedLanguage>('de');
  const [translations, setTranslations] = useState<Record<SupportedLanguage, PregnancyDayTranslation>>(withAllLanguages(undefined));
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (day === null) return;
    setError('');
    setLanguage((SUPPORTED_LANGUAGES as readonly string[]).includes(i18n.language) ? (i18n.language as SupportedLanguage) : 'de');

    if (initial && initial.day === day) {
      setTranslations(withAllLanguages(initial.translations));
      return;
    }

    let cancelled = false;
    setLoading(true);
    fetchApi(`/admin/pregnancy-days/${day}`)
      .then((res) => {
        if (!cancelled) setTranslations(withAllLanguages((res?.data ?? res)?.translations));
      })
      .catch((err) => {
        if (!cancelled) setError(err instanceof Error && err.message ? err.message : t('pregnancy:editor.loadError'));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [day, initial]);

  const update = (field: keyof PregnancyDayTranslation, value: string) =>
    setTranslations((prev) => ({ ...prev, [language]: { ...prev[language], [field]: value } }));

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (day === null) return;
    setSaving(true);
    setError('');
    try {
      const res = await fetchApi(`/admin/pregnancy-days/${day}`, { method: 'PUT', body: JSON.stringify({ translations }) });
      onSaved(res?.data ?? res);
      onClose();
    } catch (err) {
      setError(err instanceof Error && err.message ? err.message : t('pregnancy:editor.saveError'));
    } finally {
      setSaving(false);
    }
  };

  const current = translations[language];

  return (
    <Modal isOpen={day !== null} title={t('pregnancy:editor.title', { day })} onClose={onClose} size="xl">
      {loading ? (
        <div className="flex items-center justify-center gap-3 py-8 text-dark-300">
          <span className="h-5 w-5 animate-spin rounded-full border-2 border-primary-300/40 border-t-primary-300" />
          {t('common:status.loading')}
        </div>
      ) : (
        <form onSubmit={handleSave} className="space-y-4">
          <SegmentedTabs
            value={language}
            onChange={(id) => setLanguage(id as SupportedLanguage)}
            tabs={LANGUAGES.map((lang) => ({
              id: lang.id,
              label: lang.label,
              icon: isComplete(translations[lang.id]) ? undefined : (
                <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-amber-400" aria-label={t('pregnancy:editor.missingTranslation')} />
              )
            }))}
          />

          {!isComplete(current) && <p className="text-xs text-amber-300">{t('pregnancy:editor.missingTranslation')}</p>}

          <Input
            label={t('pregnancy:editor.topicLabel')}
            value={current.topic}
            maxLength={TOPIC_MAX_LENGTH}
            onChange={(e) => update('topic', e.target.value)}
          />
          <div>
            <Textarea
              label={t('pregnancy:editor.descriptionLabel')}
              value={current.description}
              rows={14}
              onChange={(e) => update('description', e.target.value)}
            />
            <p className="mt-1 text-xs text-dark-400">{t('pregnancy:editor.descriptionHint')}</p>
          </div>

          {error && <Alert type="error" message={error} />}

          <div className="modal-actions">
            <button type="button" className="btn btn-ghost" onClick={onClose}>
              {t('common:actions.cancel')}
            </button>
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? t('common:actions.saving') : t('common:actions.save')}
            </button>
          </div>
        </form>
      )}
    </Modal>
  );
}
